import env from 'env'

import type { HlsConfig, HlsListeners } from 'hls.js'

import { load, preload } from '.'

import { HlsStatic, Hls } from '../constants/types'
import { SPACE } from '../constants'

export class HLSAudio {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static Hls: any

  #audio: HTMLAudioElement
  #config: Partial<HlsConfig>
  #instance!: Hls

  get url() {
    return this.hls ? this.hls.url : null
  }

  get media() {
    return this.hls ? this.hls.media : null
  }

  get config() {
    return this.hls ? this.hls.config : null
  }

  get hls(): Hls {
    return this.#instance
  }

  get isSupported() {
    return HLSAudio.Hls.isSupported()
  }

  constructor(Hls: HlsStatic, audio: HTMLAudioElement, config: Partial<HlsConfig> = ({} as HlsConfig)) {
    HLSAudio.Hls = Hls
    this.#audio = audio
    this.#config = { ...config }
  }

  public unload() {
    if (!this.#instance) return
    
    this.#instance.stopLoad()
    this.#instance.detachMedia()
    this.#instance.destroy()
  }

  public on(event: keyof HlsListeners, callback: HlsListeners[typeof event]) {
    this.#instance?.on(event, callback)
  }

  public once(event: keyof HlsListeners, callback: HlsListeners[typeof event]) {
    this.#instance?.once(event, callback)
  }

  public off(event: keyof HlsListeners, callback: HlsListeners[typeof event]) {
    this.#instance?.off(event, callback)
  }

  public attachMedia(media: HTMLAudioElement) {
    this.#instance?.attachMedia(media)
  }

  public detachMedia() {
    this.#instance?.detachMedia()
  }

  public loadSource(src: string) {
    this.#instance?.loadSource(src)
  }

  public startLoad(position = -1) {
    this.#instance?.startLoad(position)
  }

  public stopLoad() {
    this.#instance?.stopLoad()
  }

  // Long life cycle (reuse instance)
  public set src(value: string) {
    if (!this.#instance) this.init()
    this.#instance.stopLoad()
    this.#instance.loadSource(value)
    this.#instance.attachMedia(this.#audio)
    this.#instance.startLoad()
  }

  public init() {
    this.#instance = new HLSAudio.Hls(this.#config)
    this.#instance.on(HLSAudio.Hls.Events.ERROR, (_: never, data: Record<string, unknown>): void => {
      if (data?.fatal) {
        switch (data?.type) {
          case HLSAudio.Hls.ErrorTypes.NETWORK_ERROR:
            // try to recover network error
            this.#instance.startLoad()
            break
          case HLSAudio.Hls.ErrorTypes.MEDIA_ERROR:
            this.#instance.recoverMediaError()
            break
          default:
            // cannot recover
            this.#instance.destroy()
            break
        }
      }
    })
  }

  // Short life cycle (destroyes previous instance before load)
  public async load(src: string, audio: HTMLAudioElement) {
    this.unload()

    this.init()

    await new Promise((resolve, reject) => {
      this.#instance.once(HLSAudio.Hls.Events.MANIFEST_LOADED, resolve)
      this.#instance.once(HLSAudio.Hls.Events.ERROR, reject)

      this.#instance.loadSource(src)
      this.#instance.attachMedia(audio)
      this.#instance.startLoad()
    })
  }
}

const HLS_WORKER_PATH = env.NUGSNET_CDN + '/hls/hls.worker.js'

const concat = (url: string, path: string) => (env.NUGSNET_CDN || SPACE) + '/' + url + '/' + path

const HLS_LIB_LINK = concat('hls', 'hls.min.js')

function preloadHLS() {
  return preload(HLS_LIB_LINK)
}

function loadHLS() {
  return load<HlsStatic>(HLS_LIB_LINK, 'Hls')
}


export { preloadHLS, loadHLS, concat, HLS_LIB_LINK, HLS_WORKER_PATH }

export type { HlsStatic, Hls }
